import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { softwareList, categories } from '@/mocks/software';

const tabMap: Record<string, string> = {
  software: '电脑软件',
  games: '热门游戏',
  apps: '手机应用',
  articles: '文章中心',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState(slug || 'software');

  useEffect(() => {
    if (slug) setActiveTab(slug);
  }, [slug]);

  const catTabs = [
    ...categories,
    { slug: 'articles', name: '文章中心', icon: 'ri-article-line', desc: '' },
  ];

  const currentCategory = catTabs.find((c) => c.slug === activeTab);
  const items = activeTab === 'articles'
    ? []
    : softwareList.filter((item) => item.category === activeTab);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <Breadcrumb />
      <main>
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                {currentCategory?.name || '分类浏览'}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base">
                {currentCategory?.desc || '浏览全部资源，发现更多精彩'}
              </p>
            </div>

            <div className="flex items-center justify-center mb-10">
              <div className="inline-flex bg-background-200/70 rounded-full px-1 py-1">
                {catTabs.map((tab) => (
                  <Link
                    key={tab.slug}
                    to={`/category/${tab.slug}`}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      activeTab === tab.slug
                        ? 'bg-background-50 text-foreground-900'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    <i className={`${tab.icon} mr-1.5`}></i>
                    {tab.name}
                  </Link>
                ))}
              </div>
            </div>

            {activeTab === 'articles' ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-article-line text-3xl text-primary-500"></i>
                </div>
                <h2 className="text-xl font-semibold text-foreground-800 mb-3">
                  文章中心建设中
                </h2>
                <p className="text-foreground-500 mb-6">
                  我们正在整理精彩内容，很快就会与大家见面
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors duration-200"
                >
                  <i className="ri-home-line"></i>
                  返回首页
                </Link>
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无内容</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="bg-background-50 rounded-xl p-5 border border-background-200/70 hover:border-primary-200 transition-all duration-300 group"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-500 transition-colors duration-300">
                        <i className={`${item.icon} text-lg text-primary-600 group-hover:text-white transition-colors duration-300`}></i>
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-foreground-900 truncate">{item.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-foreground-400">{item.version}</span>
                          <span className="text-xs text-foreground-300">·</span>
                          <span className="text-xs text-foreground-400">{item.size}</span>
                        </div>
                      </div>
                      {item.isNew && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-accent-100 text-accent-700 flex-shrink-0">
                          NEW
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-foreground-500 leading-relaxed mb-4 line-clamp-2">
                      {item.desc}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-foreground-400">{item.platform}</span>
                      </div>
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap">
                        <i className="ri-download-2-line"></i>
                        下载
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}